import { useEffect } from "react";
import {
  Stack,
  Title,
  ActionIcon,
  Tooltip,
  Table,
  Loader,
} from "@mantine/core";
import { IconStarFilled, IconStar } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import useApi from "../utils/api/useApi";
import coreApi from "../utils/api/coreApi";
import { Application } from "../utils/api/api_types";
import { useState } from "react";
import { STANDARD_ERROR_MESSAGE } from "../utils/constants";
import { useMediaQuery } from "@mantine/hooks";
import { MOBILE_VIEW_MAX_WIDTH_EM } from "../Settings";

type LoadingAppStates = {
  toggleFavourite: Record<number, boolean>;
};

export default function ManageApps() {
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_VIEW_MAX_WIDTH_EM})`);

  const appsRes = useApi<Application[]>({
    apiFunc: coreApi.applicationsList,
    defaultData: [],
    paginated: false,
  });
  const [appsData, setAppsData] = useState<Application[]>(
    appsRes.responseData.data
  );
  useEffect(() => {
    setAppsData(appsRes.responseData.data);
  }, [JSON.stringify(appsRes.responseData.data)]);

  const [loadingStates, setLoadingStates] = useState<LoadingAppStates>({
    toggleFavourite: {},
  });
  const handleToggleFavourite = async (id: number, index: number) => {
    try {
      setLoadingStates((prev) => ({
        ...prev,
        toggleFavourite: { ...prev.toggleFavourite, [index]: true },
      }));

      const res = await coreApi.applicationsToggleFavourite(id);
      const app = res.data;

      notifications.show({
        title: `${
          app.is_favourite
            ? "⭐️ Added App to Favourites"
            : "🔴 Removed App from Favourites"
        }`,
        message: `${app.name.replace(/_/g, " ")} ${
          app.is_favourite ? "added to favourites" : "removed from favourites"
        }`,
        color: app.is_favourite ? "green" : "yellow",
      });

      setAppsData((prevApps) =>
        prevApps.map((item) => (item.id === app.id ? app : item))
      );
    } catch (e) {
      console.log(e);
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        toggleFavourite: { ...prev.toggleFavourite, [index]: false },
      }));
    }
  };

  const appRows = appsData.map((app, i) => (
    <Table.Tr key={i}>
      <Table.Td>{app.name.replace(/_/g, " ")}</Table.Td>
      <Table.Td>
        <Tooltip
          label={
            app.is_favourite ? "Remove From Favourites" : "Add To Favourites"
          }
        >
          <ActionIcon
            variant="transparent"
            color="yellow"
            onClick={() => {
              handleToggleFavourite(app.id, i);
            }}
            loading={loadingStates.toggleFavourite[i]}
          >
            {app.is_favourite ? <IconStarFilled /> : <IconStar />}
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack
      align="center"
      justify="flex-start"
      mx="auto"
      w={!isMobile ? "50%" : "auto"}
    >
      <Title order={3} mt="xl">
        Manage Applications
      </Title>

      {appsRes.loading ? (
        <Stack
          align="center"
          justify="center"
          style={{ height: "calc(100vh - 210px)" }}
        >
          <Loader type="bars" />
        </Stack>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name </Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{appRows}</Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
